import { Component, computed, effect, inject, signal, untracked } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import { KENDO_GRIDLAYOUT } from '@progress/kendo-angular-layout';
import type { PageChangeEvent } from '@progress/kendo-angular-listview';
import { AgreementDetailedDto, AgreementsClient, FacetsClient, UnityWorkflowState } from '@unity/clients';
import { ListAgreementSharedComponent } from '@unity/components/shared/list-agreement-shared/list-agreement-shared.component';
import { PageContainerComponent } from '@unity/layouts/page-container/page-container.component';
import { FILTERSTATE_KEY, FilterStateService } from '@unity/services/filter-state.service';
import type { FilterItemToggleEvent, FilterMasterStateEvent, SwitchStateEvent } from '@unity/services/gridify.service';
import { GridifyService } from '@unity/services/gridify.service';
import { SessionService } from '@unity/services/session.service';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { FilterState, SWITCHES_KEY } from '@unity/utils/filter-state';
import { QuickActions } from '@unity/utils/quick-actions';
import { GridifyQueryBuilder } from 'gridify-client';

// Switches have special rendering so they need special config
export const AgreementListSwitches = {
  isAssigned: {
    item: 'is_mine',
    state: true,
  } as SwitchStateEvent<AgreementDetailedDto>,
  isOverdue: {
    item: 'is_overdue',
    state: false,
  } as SwitchStateEvent<AgreementDetailedDto>,
};

// Overrides for the default state returned on first load / when filter is cleared.
// Key must correspond to facet property name.
const AgreementFilterDefaults = {
  ['CurrentState.Name']: {
    [UnityWorkflowState.Archived.toString()]: false,
  },
} as Record<string, Record<string, boolean>>;

@Component({
  selector: 'unity-list-agreement-page',
  imports: [KENDO_GRIDLAYOUT, PageContainerComponent, ListAgreementSharedComponent, ActionSharedComponent],
  templateUrl: './list-agreement-page.component.html',
  styleUrl: './list-agreement-page.component.scss',
  providers: [{ provide: FILTERSTATE_KEY, useValue: 'agreementFilter' }, FilterStateService<AgreementDetailedDto>],
})
export class ListAgreementPageComponent {
  private readonly sessionService = inject(SessionService);
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly facetsClient = inject(FacetsClient);
  private readonly filterStateService = inject(FilterStateService<AgreementDetailedDto>);
  private readonly gridifyService = inject(GridifyService<AgreementDetailedDto>);

  /* Data */
  private readonly defaultQueryOptions = {
    page: 1,
    pageSize: 10,
  };

  private readonly switches = [AgreementListSwitches.isAssigned, AgreementListSwitches.isOverdue];
  protected createAgreementAction = QuickActions.CreateAgreement;

  /* Resources */
  private readonly facetsResource = rxResource({
    stream: () => this.facetsClient.getAllForAgreement(),
  });

  private agreementsResource = rxResource({
    params: () => {
      const query = this.$query();
      return query ? { query } : undefined;
    },
    stream: ({ params: { query } }) => this.agreementsClient.getAllAsDetailed(query.page, query.pageSize, query.orderBy, query.filter),
  });

  /* Signals */
  private readonly $page = signal(this.defaultQueryOptions.page);
  private readonly $pageSize = signal(this.defaultQueryOptions.pageSize);

  private readonly $query = computed(() => {
    // Copy to avoid mutating signal internals
    const filterState = [...this.filterStateService.$states()];
    const page = this.$page();
    const pageSize = this.$pageSize();

    let builder = new GridifyQueryBuilder();

    // Pull out the switches block
    const switchesIx = filterState.findIndex(x => x.key == SWITCHES_KEY);
    const switchState = filterState.splice(switchesIx, 1)[0];
    if (!switchState) {
      // Service not initialized yet
      return;
    }
    const switchSnapshot = this.parseSnapshotFromSwitchState(switchState);
    const hasSwitches = Object.entries(switchSnapshot).length > 0;
    if (hasSwitches) {
      builder = this.gridifyService.buildSwitchesQuery(builder, switchSnapshot);
    }

    // Skip value filter if all values are checked (would be a no-op)
    const valueFilters = filterState.filter(pFilter => !pFilter.isAll());
    builder = this.gridifyService.buildFilterQuery(builder, valueFilters, hasSwitches);
    builder.setPage(page);
    builder.setPageSize(pageSize);
    return builder.build();
  });

  private readonly $facets = computed(() => {
    if (!this.facetsResource.hasValue()) {
      return;
    }
    return this.facetsResource.value().facets;
  });

  protected readonly $valueStates = this.filterStateService.$valueStates;
  protected readonly $showAgencyName = this.sessionService.$isInternalUserRole;

  protected readonly $agreements = this.agreementsResource.value;
  protected readonly $isLoading = this.agreementsResource.isLoading;

  protected readonly $switchStates = computed(() => this.filterStateService.$switchStates() ?? this.switches);
  protected readonly $count = computed(() => this.$agreements()?.count ?? 0);
  protected readonly $total = computed(() => this.$agreements()?.total ?? 0);

  /* Constructor */

  public constructor() {
    effect(() => {
      const facets = this.$facets();
      if (!facets) {
        return;
      }
      this.filterStateService.buildFilters(facets, this.switches);
      this.setDefaults();
    });
  }

  /* Methods */

  // toggle-over filter state with configured page defaults
  private setDefaults() {
    Object.entries(AgreementFilterDefaults).forEach(([prop, state]) =>
      Object.entries(state).forEach(([key, value]) => this.filterStateService.toggle(prop as keyof AgreementDetailedDto, key, value))
    );
  }

  // HACK:
  // "IsAssigned = False" or "IsOverdue = False" actually mean "Do Not Filter", so remove from the query.
  // If this gets extended, consider allowing switches to set an indeterminate value and use that to remove the filter key at an earlier stage.
  private parseSnapshotFromSwitchState(switchState: FilterState<AgreementDetailedDto>) {
    let switchSnapshot = switchState.getMapSnapshot();
    for (const key of [AgreementListSwitches.isAssigned.item, AgreementListSwitches.isOverdue.item]) {
      if (switchState.get(key) === false) {
        const withoutSwitch = Object.entries(switchSnapshot).filter(([k, _]) => k !== key);
        switchSnapshot = Object.fromEntries(withoutSwitch);
      }
    }
    return switchSnapshot;
  }

  protected handlePageEvent(event: PageChangeEvent) {
    this.$page.set(1 + Math.floor(event.skip / event.take));
    this.$pageSize.set(event.take);
  }

  protected handleClearEvent() {
    this.switches.forEach(s => this.filterStateService.toggleSwitch({ item: s.item, state: false }));
    const facets = untracked(this.$facets);
    facets?.forEach(facet => this.filterStateService.toggleAll(facet.property as keyof AgreementDetailedDto, true));
    this.setDefaults();
  }

  protected handleMasterEvent(event: FilterMasterStateEvent<AgreementDetailedDto>) {
    this.filterStateService.toggleAll(event.key, event.state);
  }

  protected handleItemToggleEvent(event: FilterItemToggleEvent<AgreementDetailedDto>) {
    this.filterStateService.toggle(event.key, event.item);
  }

  protected handleSwitchStateEvent(event: SwitchStateEvent<AgreementDetailedDto>) {
    this.filterStateService.toggleSwitch(event);
  }
}
